import type { WorkspaceTaskSummary } from '@/features/tasks';
import {
  NEEDS_PERSON_STATES,
  type TaskListState,
} from '@/features/workspace-tasks/listView';

/**
 * The rest of the workspace (spec 153 US3): every task once, by what it waits
 * on, each part a link to the Tasks page list that holds exactly its tasks.
 *
 * The parts are disjoint by task and sum to `counts.tasks`:
 * - **With you**: Your turn's total. Needs attention, waiting on a person and
 *   parents with subtasks to start, counted once here as they are there.
 * - **Waiting on subtasks**: parents whose run waits on its subtasks, less the
 *   ones with subtasks to start, which are already with you.
 * - **Running**: an agent executing.
 * - **At rest**: nothing happening and nobody needed; queued runs included,
 *   since nothing runs yet (spec 116 FR-006).
 *
 * Each part's `states` is the Tasks page filter whose list has its count
 * (FR-018); `your-turn-agreement.test.ts` holds that on every fixture.
 */

export type RestKey = 'with-you' | 'subtasks' | 'running' | 'at-rest';

export interface RestPart {
  key: RestKey;
  value: number;
  /** The Tasks page's `state=` filter that lists exactly these tasks. */
  states: readonly TaskListState[];
}

export interface RestOfWorkspace {
  /** The parts with tasks in them, in reading order. */
  parts: RestPart[];
  total: number;
}

export function restOfWorkspace(
  summary: Pick<WorkspaceTaskSummary, 'attention' | 'counts'>,
): RestOfWorkspace {
  const { attention, counts } = summary;
  const toStart = attention.toStart.length;
  const all: RestPart[] = [
    {
      key: 'with-you',
      value:
        attention.needsAttention.length + attention.waiting.length + toStart,
      // Read here, at call time, never at module load: `listView` sits on an
      // import cycle with this package (spec 152).
      states: NEEDS_PERSON_STATES,
    },
    {
      key: 'subtasks',
      value: counts.waitingOnSubtasks - toStart,
      states: ['subtasks'],
    },
    { key: 'running', value: counts.running, states: ['running'] },
    { key: 'at-rest', value: counts.atRest, states: ['at-rest'] },
  ];
  return {
    parts: all.filter((part) => part.value > 0),
    total: counts.tasks,
  };
}
